import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { LanguageContext } from '../utils/language-provider';
import { isMobileDevice } from '../utils/mobile-detection';

const PopupOverlay = styled.div<{ $isVisible: boolean }>`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${props => props.theme.colors.primary80};
    display: ${props => props.$isVisible ? 'flex' : 'none'};
    justify-content: center;
    z-index: ${props => props.theme.zIndex.max};
    padding: 15px;
`;

const PopupContent = styled.div`
    background: ${props => props.theme.colors.white};
    border-radius: 12px;
    padding: 12px;
    max-width: 400px;
    height: fit-content;
    text-align: center;
    box-shadow: 0 10px 30px ${props => props.theme.colors.primary80};
`;

const PopupMessage = styled.p`
    margin: 0 0 15px 0;
    color: ${props => props.theme.colors.gray3};
    line-height: 1.2;
    font-size: ${props => props.theme.typography.md};
    font-weight: bold;
`;

const CloseButton = styled.button`
    background: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.white};
    border: none;
    border-radius: 6px;
    padding: 12px 24px;
    font-size: 14px;
    font-weight: bold;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
        background: ${props => props.theme.colors.primaryDark};
    }
`;

interface OrientationPopupProps {
    onClose?: () => void;
}

export const OrientationPopup: React.FC<OrientationPopupProps> = ({ onClose }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [userDismissed, setUserDismissed] = useState(false);
    const { translations } = useContext(LanguageContext);

    // Check orientation
    const checkOrientation = () => {
        let isPortrait;
        if (screen.orientation && screen.orientation.type) {
            isPortrait = screen.orientation.type.startsWith('portrait');
        } else {
            isPortrait = screen.height > screen.width;
        }

        if (isMobileDevice()) {
            // Show popup only if in portrait and user hasn't dismissed it
            setIsVisible(isPortrait && !userDismissed);
            // Reset userDismissed when switching to landscape
            if (!isPortrait) {
                setUserDismissed(false);
            }
        } else {
            setIsVisible(false);
        }
    };

    useEffect(() => {
        checkOrientation();

        const handleResize = () => {
            checkOrientation();
        };

        const handleOrientationChange = () => {
            // Small delay to let the browser update dimensions
            setTimeout(checkOrientation, 100);
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleOrientationChange);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleOrientationChange);
        };
    }, []);

    const handleClose = () => {
        setIsVisible(false);
        setUserDismissed(true);
        if (onClose) {
            onClose();
        }
    };

    return (
        <PopupOverlay $isVisible={isVisible}>
            <PopupContent>
                <PopupMessage>
                    {translations.orientationPopup?.message ||
                        'Bitte ins Querformat drehen, um alle Funktionen optimal zu nutzen.'}
                </PopupMessage>
                <CloseButton onClick={handleClose}>
                    {translations.orientationPopup?.closeButton || 'Verstanden'}
                </CloseButton>
            </PopupContent>
        </PopupOverlay>
    );
};

export default OrientationPopup;